import StagingSearchList from './StagingSearchList';
import StagingNotifications from './StagingNotifications';
import React, { useEffect, useState } from 'react';
import StagingOverview from './StagingOverview';
import StagingProviders from './StagingProviders';
import StagingClients from './StagingClients';
import StagingBookings from './StagingBookings';
import StagingPayments from './StagingPayments';
import StagingReviews from './StagingReviews';
import StagingPromotions from './StagingPromotions';
import StagingSupport from './StagingSupport';
import './StagingWorkspace.css';
import './StagingResponsive.css';
import './StagingDesignSystem.css';

const sections = [
  ['overview', '◫', 'Overview'],
  ['providers', '♧', 'Service providers'],
  ['clients', '♙', 'Clients'],
  ['bookings', '▦', 'Bookings'],
  ['payments', '⇄', 'Payments'],
  ['reviews', '☆', 'Reviews'],
  ['promotions', '◇', 'Promotions'],
  ['categories', '☷', 'Categories'],
  ['support', '◷', 'Support'],
  ['search', '⌕', 'Search list'],
  ['notifications', '✉', 'Notifications'],
  ['settings', '⚙', 'Settings']
];


export default function StagingWorkspace() {
  const [page, setPage] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEscape = event => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onEscape);
    };
  }, [mobileMenuOpen]);
  const [providerTab, setProviderTab] = useState('live');
  const [bookingInitialTab, setBookingInitialTab] = useState('All');
  const [bookingDateRange, setBookingDateRange] = useState(null);
  const [providerCohort, setProviderCohort] = useState(null);
  const [providerCategory, setProviderCategory] = useState('');
  const openProviders = (tab='live', cohort=null, category='') => {
    setProviderCategory(category);
    setProviderTab(tab);
    setProviderCohort(cohort);
    setPage('providers');
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const openPage = key => {
    if (key === 'providers') { setProviderTab('live'); setProviderCohort(null); setProviderCategory(''); }
    if (key === 'bookings') { setBookingInitialTab('All'); setBookingDateRange(null); }
    setPage(key);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div className="bs-unified">
      <header className="bs-unified-mobile-header">
        <div className="bs-unified-mobile-brand">
          <span className="bs-unified-mark">b.</span>
          <span>beauty spot<small>ADMIN SPACE</small></span>
        </div>
        <button
          type="button"
          className="bs-unified-menu-toggle"
          aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileMenuOpen}
          aria-controls="bs-mobile-navigation"
          onClick={() => setMobileMenuOpen(open => !open)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
      </header>

      {mobileMenuOpen && <button
        type="button"
        className="bs-unified-menu-backdrop"
        aria-label="Close navigation"
        onClick={() => setMobileMenuOpen(false)}
      />}

      <aside
        id="bs-mobile-navigation"
        className={`bs-unified-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="bs-unified-brand">
          <span className="bs-unified-mark">b.</span>
          <span>beauty spot<small>ADMIN SPACE</small></span>
        </div>

        <div className="bs-unified-label">WORKSPACE</div>

        <nav>
          {sections.map(([key, icon, name]) => (
            <button
              key={key}
              className={page === key ? 'active' : ''}
              onClick={() => openPage(key)}
            >
              <span className="bs-unified-icon">{icon}</span>
              {name}
            </button>
          ))}
        </nav>

        <div className="bs-unified-bottom">
          <strong>Beauty Spot</strong>
          <small>Staging admin workspace</small>
        </div>
      </aside>

      <div className="bs-unified-content">
        {page === 'overview' && <StagingOverview onOpenProviders={openProviders} onOpenClients={()=>openPage('clients')} onOpenSupport={()=>openPage('support')} onOpenAwaitingBookings={range=>{setBookingDateRange(range);setBookingInitialTab('Awaiting completion');setPage('bookings');setMobileMenuOpen(false);window.scrollTo(0,0);}} />}
        {page === 'providers' && <StagingProviders initialTab={providerTab} cohort={providerCohort} initialCategory={providerCategory} />}
        {page === 'clients' && <StagingClients />}
        {page === 'bookings' && <StagingBookings initialTab={bookingInitialTab} initialDateRange={bookingDateRange} />}
        {page === 'payments' && <StagingPayments />}
        {page === 'reviews' && <StagingReviews />}
        {page === 'promotions' && <StagingPromotions />}
        {page === 'support' && <StagingSupport />}
        {page === 'search' && <StagingSearchList />}
        {page === 'notifications' && <StagingNotifications />}
        {!['overview','providers','clients','bookings','payments','reviews','promotions','support','search','notifications'].includes(page) && (
          <div className={`bs-unified-preview ${page === "notifications" ? "bs-notifications-preview" : ""}`}>
            <div className="bs-unified-notice">
              DESIGN PREVIEW ONLY — This section contains fictional sample data.
            </div>
            <iframe
              key={page}
              title="Beauty Spot admin prototype"
              src="/prototype-preview/index.html?v=1793654344"
              onLoad={event => {
                const labels = {
                  payments: 'Payments', categories: 'Categories',
                  support: 'Support', search: 'Search list',
                  notifications: 'Notifications', settings: 'Settings'
                };
                const label = labels[page];
                if (!label) return;
                try {
                  const doc = event.currentTarget.contentDocument;
                  if (!doc) return;
                  // Keep staging navigation; hide only the embedded prototype shell.
                  if (!doc.getElementById('bs-embedded-prototype-style')) {
                    const style = doc.createElement('style');
                    style.id = 'bs-embedded-prototype-style';
                    style.textContent = `
                      body > aside.sidebar { display: none !important; }
                      body > .workspace { margin-left: 0 !important; width: 100% !important;
                        max-width: none !important; min-width: 0 !important; }
                      body > .workspace > header { display: none !important; }
                      body > .workspace > main { width: 100% !important; max-width: none !important;
                        margin: 0 !important; box-sizing: border-box !important; }
                    `;
                    doc.head.appendChild(style);

                  }
                  const button = Array.from(doc.querySelectorAll('[data-roadmap]'))
                    .find(el => el.getAttribute('data-roadmap') === label);
                  if (button) button.click();
                  // Notifications: expand the embedded document to its actual content height.
                  // Scroll the outer workspace, not a clipped iframe viewport.
                  if (page === 'notifications') {
                    const frame = event.currentTarget;
                    frame.style.flex = 'none';
                    frame.style.display = 'block';
                    frame.style.overflow = 'hidden';
                    frame.setAttribute('scrolling', 'no');
                    const resizeFrame = () => {
                      const body = doc.body;
                      const html = doc.documentElement;
                      if (!body || !html || !frame.isConnected) return;
                      const height = Math.max(
                        window.innerHeight,
                        body.scrollHeight,
                        html.scrollHeight,
                        body.offsetHeight,
                        html.offsetHeight
                      );
                      frame.style.height = `${Math.max(1, height + 8)}px`;
                    };
                    // Allow the prototype's section switch to finish rendering.
                    requestAnimationFrame(() => {
                      resizeFrame();
                      const observer = new ResizeObserver(resizeFrame);
                      observer.observe(doc.body);
                      const main = doc.querySelector('.workspace > main');
                      if (main) observer.observe(main);
                      doc.defaultView.addEventListener('resize', resizeFrame);
                      window.addEventListener('resize', resizeFrame, { passive: true });
                      // Disconnect observers if this iframe is replaced.
                      const cleanup = new MutationObserver(() => {
                        if (!frame.isConnected) {
                          observer.disconnect();
                          cleanup.disconnect();
                          window.removeEventListener('resize', resizeFrame);
                        }
                      });
                      cleanup.observe(document.body, { childList: true, subtree: true });
                    });
                  }

                  else console.warn('Prototype section unavailable:', label);
                } catch (error) {
                  console.warn('Prototype navigation could not be selected:', error);
                }
              }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-downloads"
            />
          </div>
        )}
      </div>
    </div>
  );
}
